import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Filter } from "lucide-react";
import { QueryGroup, QueryCondition, isQueryGroup, isQueryCondition } from "@/types/queryBuilder";
import { ConditionRow } from "./ConditionRow";
import { cn } from "@/lib/utils";

interface ConditionGroupProps {
	group: QueryGroup;
	depth?: number;
	isRoot?: boolean;
	onTypeChange: (type: 'AND' | 'OR') => void;
	onAddCondition: () => void;
	onAddGroup: (type: 'AND' | 'OR') => void;
	onRemove?: () => void;
	onUpdateCondition: (id: string, updates: Partial<QueryCondition>) => void;
	onRemoveCondition: (id: string) => void;
	onUpdateGroupType: (id: string, type: 'AND' | 'OR') => void;
	onRemoveGroup: (id: string) => void;
	onAddConditionToGroup: (groupId: string) => void;
	onAddGroupToGroup: (parentId: string, type: 'AND' | 'OR') => void;
}

export function ConditionGroup({
	group,
	depth = 0,
	isRoot = false,
	onTypeChange,
	onAddCondition,
	onAddGroup,
	onRemove,
	onUpdateCondition,
	onRemoveCondition,
	onUpdateGroupType,
	onRemoveGroup,
	onAddConditionToGroup,
	onAddGroupToGroup
}: ConditionGroupProps) {
	const { t } = useTranslation();

	const bgColors = [
		'bg-blue-50 dark:bg-blue-950/30',
		'bg-purple-50 dark:bg-purple-950/30',
		'bg-green-50 dark:bg-green-950/30',
		'bg-orange-50 dark:bg-orange-950/30'
	];
	const borderColors = [
		'border-blue-200 dark:border-blue-800',
		'border-purple-200 dark:border-purple-800',
		'border-green-200 dark:border-green-800',
		'border-orange-200 dark:border-orange-800'
	];
	const accentColors = [
		'text-blue-500 dark:text-blue-400',
		'text-purple-600 dark:text-purple-400',
		'text-green-600 dark:text-green-400',
		'text-orange-600 dark:text-orange-400'
	];

	const colorIndex = depth % bgColors.length;

	return (
		<div className={cn(
			"rounded-sm border p-4",
			bgColors[colorIndex],
			borderColors[colorIndex],
			depth > 0 && "mt-3"
		)}>
			{/* Group header */}
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-3 flex-wrap">
					{isRoot && (
						<div className={cn("flex items-center gap-2", accentColors[colorIndex])}>
							<Filter className="h-4 w-4" />
							<span className="font-medium">
								{t("queries.groups.filterBy", "Filter by")}
							</span>
						</div>
					)}
					<div className="inline-flex rounded-sm border border-input bg-background p-0.5">
						<button
							type="button"
							className={cn(
								"px-3 py-1.5 text-sm font-medium rounded-sm transition-all",
								group.type === 'AND'
									? "bg-primary text-primary-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground hover:bg-muted"
							)}
							onClick={() => onTypeChange('AND')}
						>
							{t("queries.groups.all", "ALL")}
						</button>
						<button
							type="button"
							className={cn(
								"px-3 py-1.5 text-sm font-medium rounded-sm transition-all",
								group.type === 'OR'
									? "bg-primary text-primary-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground hover:bg-muted"
							)}
							onClick={() => onTypeChange('OR')}
						>
							{t("queries.groups.any", "ANY")}
						</button>
					</div>
					<span className="text-sm text-muted-foreground">
						{group.type === 'AND'
							? t("queries.groups.allConditions", "of the following conditions")
							: t("queries.groups.anyCondition", "of the following conditions")}
					</span>
				</div>
				{!isRoot && onRemove && (
					<Button
						variant="ghost"
						size="sm"
						className="h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
						onClick={onRemove}
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				)}
			</div>

			{/* Conditions and nested groups */}
			<div className="space-y-2">
				{group.conditions.length === 0 ? (
					<div className="text-center py-6 text-muted-foreground text-sm border-2 border-dashed rounded-sm bg-background/50">
						{t("queries.groups.noConditions", "No conditions yet. Add a condition to start filtering.")}
					</div>
				) : (
					group.conditions.map((item, index) => {
						if (isQueryCondition(item))
							return (
								<div key={item.id} className="relative">
									{index > 0 && (
										<div className={cn(
											"absolute -top-3 left-4 px-2 py-0.5 text-xs font-medium rounded bg-background border",
											accentColors[colorIndex]
										)}>
											{group.type}
										</div>
									)}
									<ConditionRow
										condition={item}
										onUpdate={(updates) => onUpdateCondition(item.id, updates)}
										onRemove={() => onRemoveCondition(item.id)}
									/>
								</div>
							);

						if (isQueryGroup(item))
							return (
								<div key={item.id} className="relative">
									{index > 0 && (
										<div className={cn(
											"absolute -top-1 left-4 px-2 py-0.5 text-xs font-medium rounded bg-background border z-10",
											accentColors[colorIndex]
										)}>
											{group.type}
										</div>
									)}
									<ConditionGroup
										group={item}
										depth={depth + 1}
										onTypeChange={(type) => onUpdateGroupType(item.id, type)}
										onAddCondition={() => onAddConditionToGroup(item.id)}
										onAddGroup={(type) => onAddGroupToGroup(item.id, type)}
										onRemove={() => onRemoveGroup(item.id)}
										onUpdateCondition={onUpdateCondition}
										onRemoveCondition={onRemoveCondition}
										onUpdateGroupType={onUpdateGroupType}
										onRemoveGroup={onRemoveGroup}
										onAddConditionToGroup={onAddConditionToGroup}
										onAddGroupToGroup={onAddGroupToGroup}
									/>
								</div>
							);

						return null;
					})
				)}
			</div>

			{/* Add buttons */}
			<div className="flex gap-2 mt-4 pt-3 border-t border-dashed">
				<Button
					variant="outline"
					size="sm"
					className="bg-background"
					onClick={onAddCondition}
				>
					<Plus className="h-4 w-4 mr-1" />
					{t("queries.groups.addCondition", "Add Condition")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="bg-background"
					onClick={() => onAddGroup('AND')}
				>
					<Plus className="h-4 w-4 mr-1" />
					{t("queries.groups.addGroup", "Add Group")}
				</Button>
			</div>
		</div>
	);
}
